import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-mobile-header',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, RouterModule],
  templateUrl: './mobile-header.component.html',
  styleUrl: './mobile-header.component.scss',
})
export class MobileHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;

  pageTitle = 'Home';
  showBackButton = false;

  private readonly destroy$ = new Subject<void>();
  private currentUrl = '';

  private readonly pageTitles: Record<string, string> = {
    projects: 'Home',
    notes: 'Notes',
    // Add more headers here as we get more screens.
  };

  constructor(
    private readonly router: Router,
    private readonly location: Location
  ) {}

  ngOnInit(): void {
    this.updateRouteState(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.updateRouteState(event.urlAfterRedirects));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && !changes['visible'].currentValue) {
      this.showBackButton = false;
    } else if (changes['visible']?.currentValue) {
      this.updateRouteState(this.router.url);
    }
  }

  goBack(): void {
    const currentUrl = this.router.url;
    if (/^\/batches\/[^/]+\/notes\/entry(?:\?|$)/.test(currentUrl)) {
      const [path, query = ''] = currentUrl.split('?');
      const queryParams = new URLSearchParams(query);
      queryParams.delete('noteId');
      const notesPath = path.replace('/notes/entry', '/notes');
      const notesUrl = queryParams.toString() ? `${notesPath}?${queryParams.toString()}` : notesPath;
      this.router.navigateByUrl(notesUrl);
      return;
    }

    if (/^\/batches\/[^/]+\/notes(?:\?|$)/.test(currentUrl)) {
      this.router.navigate(['/projects']);
      return;
    }

    this.location.back();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateRouteState(url: string): void {
    this.currentUrl = url.split('?')[0].split('#')[0];
    const segments = this.currentUrl.split('/').filter(Boolean);
    const rootRoute = segments[0] || 'projects';
    const isNotesPage = segments[0] === 'batches' && segments[2] === 'notes';
    const isNoteEditor = isNotesPage && segments[3] === 'entry';
    const isEditingNote = isNoteEditor && new URLSearchParams(url.split('?')[1] || '').has('noteId');

    this.pageTitle = isNoteEditor
      ? isEditingNote
        ? 'Edit Note'
        : 'Create Note'
      : isNotesPage
        ? 'Notes'
        : this.pageTitles[rootRoute] || this.toTitleCase(rootRoute);
    this.showBackButton = segments.length > 1 || this.currentUrl.includes('/batch');
  }

  private toTitleCase(value: string): string {
    return value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
  }
}
